import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Phone, Globe } from "lucide-react";
import { useAuth } from "../../common/store/authStore";
import Alert from "../../components/alert";
import ChedaGamingSelect from "../../components/form/cheda-gaming-select";
import { COUNTRIES } from "../../common/constants/countries";
import "./styles/signup.css";
import { signup } from "../../common/services/auth";
import { AlertState, SignUpForm } from "../../common/services/auth.types";
import * as Yup from "yup";
import { ChedaGamingForm } from "../../components/form/cheda-gaming-form";
import ChedaGamingInput from "../../components/form/cheda-gaming-input";
import ChedGamingButton from "../../components/cheda-gaming-button";
import ChedaGamingPasswordInput from "../../components/form/cheda-gaming-password-input";
import Footer from "../../components/footer";
import Navbar from "../../components/navbar";

function SignUp() {
  useEffect(() => {
    document.title = "Sign Up - Cheda Gaming";
  }, []);

  const [alert, setAlert] = useState<AlertState | null>(null);

  const navigate = useNavigate();
  const { setAuthenticatedUser, setAccessToken, setRefreshToken } = useAuth();

  const initialValues = {
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    phone: "",
    country: "",
    password: "",
    passwordConfirmation: "",
    termsOfService: false,
  };

  const showAlert = (
    type: AlertState["type"],
    message: string,
    title?: string | null,
  ) => {
    setAlert({ type, message, title });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  // Formik submit handler
  const handleSubmit = async (values: typeof initialValues, helpers: any) => {
    if (values.password !== values.passwordConfirmation) {
      showAlert(
        "error",
        "Password and confirm password do not match",
        "Password Mismatch",
      );
      return helpers.setSubmitting(false);
    }

    if (!values.termsOfService) {
      showAlert(
        "error",
        "You must agree to the Terms of Service",
        "Terms Required",
      );
      return helpers.setSubmitting(false);
    }

    try {
      const payload: SignUpForm = {
        firstName: values.firstName,
        lastName: values.lastName,
        username: values.username,
        email: values.email,
        phoneNumber: values.phone,
        password: values.password,
        country: values.country,
      };

      const response = await signup(payload);

      setAuthenticatedUser(response.user);
      setAccessToken(response.accessToken);
      setRefreshToken(response.refreshToken);

      showAlert(
        "success",
        "Account created successfully! Please verify your email.",
        "Welcome to Cheda Gaming",
      );

      // TEMP: Skip OTP redirect, always log in
      navigate('/tournaments');
    } catch (error: any) {
      showAlert("error", error?.message || "Sign Up Failed", "Sign Up Failed");
    } finally {
      helpers.setSubmitting(false);
    }
  };

  // validation schema
  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    username: Yup.string()
      .required("Username is required")
      .matches(/^\S+$/, "No spaces allowed"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone number is required"),
    country: Yup.string().required("Country is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
    termsOfService: Yup.boolean().oneOf([true], "You must accept the terms"),
  });

  return (
    <div className="signup-page page-with-navbar-footer">
      <Navbar />

      <div className="signup-background">
        <div className="background-overlay"></div>
      </div>

      <div className="signup-container">
        <div className="signup-card">
          <div className="signup-header">
            <div className="logo-container">
              <img
                src="/images/chedagaming_logo.png"
                alt="Cheda Gaming"
                className="signup-logo"
              />
            </div>
            <h1 className="signup-title">Join the Battle, Gamer</h1>
            <p className="signup-subtitle">
              Create your account and start your gaming journey
            </p>
          </div>

          <ChedaGamingForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {(formik) => (
              <>
                <div className="name-fields">
                  <ChedaGamingInput
                    name="firstName"
                    label="First Name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                    touched={formik.touched.firstName}
                    error={formik.errors.firstName as string | undefined}
                    icon={<User className="input-icon" size={20} />}
                    placeholder="Enter your first name"
                    required
                    normalize={(v) => v.replace(/\s+/g, "")}
                    disabled={formik.isSubmitting}
                  />
                  <ChedaGamingInput
                    name="lastName"
                    label="Last Name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                    touched={formik.touched.lastName}
                    error={formik.errors.lastName as string | undefined}
                    icon={<User className="input-icon" size={20} />}
                    placeholder="Enter your last name"
                    required
                    normalize={(v) => v.replace(/\s+/g, "")}
                    disabled={formik.isSubmitting}
                  />
                </div>
                <ChedaGamingInput
                  name="username"
                  label="Username/Gaming Tag"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.username}
                  touched={formik.touched.username}
                  error={formik.errors.username as string | undefined}
                  icon={<User className="input-icon" size={20} />}
                  placeholder="Enter your gaming tag"
                  required
                  normalize={(v) => v.replace(/\s+/g, "")}
                  disabled={formik.isSubmitting}
                />

                <ChedaGamingInput
                  name="email"
                  label="Email Address"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  touched={formik.touched.email}
                  error={formik.errors.email as string | undefined}
                  icon={<Mail className="input-icon" size={20} />}
                  placeholder="Enter your email address"
                  required
                  normalize={(v) => v.replace(/\s+/g, "")}
                  disabled={formik.isSubmitting}
                />

                <ChedaGamingInput
                  name="phone"
                  label="Phone Number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                  touched={formik.touched.phone}
                  error={formik.errors.phone as string | undefined}
                  icon={<Phone className="input-icon" size={20} />}
                  placeholder="Enter your phone number"
                  required
                  normalize={(v) => v.replace(/\s+/g, "")}
                  disabled={formik.isSubmitting}
                  keyboardType="numeric"
                />

                <ChedaGamingSelect
                  name="country"
                  label="Country"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.country}
                  touched={formik.touched.country}
                  error={formik.errors.country as string | undefined}
                  icon={<Globe className="input-icon" size={20} />}
                  options={COUNTRIES}
                  placeholder="Select your country"
                  required
                  disabled={formik.isSubmitting}
                />

                <ChedaGamingPasswordInput
                  name="password"
                  label="Password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  touched={formik.touched.password}
                  error={formik.errors.password as string | undefined}
                  icon={<Lock className="input-icon" size={20} />}
                  placeholder="Enter your password"
                  required
                  disabled={formik.isSubmitting}
                />

                <ChedaGamingPasswordInput
                  name="passwordConfirmation"
                  label="Confirm Password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.passwordConfirmation}
                  touched={formik.touched.passwordConfirmation}
                  error={
                    formik.errors.passwordConfirmation as string | undefined
                  }
                  icon={<Lock className="input-icon" size={20} />}
                  placeholder="Confirm your password"
                  required
                  disabled={formik.isSubmitting}
                />

                <div className="form-group terms-group">
                  <label className="terms-label">
                    <input
                      type="checkbox"
                      checked={formik.values.termsOfService}
                      onChange={(e) =>
                        formik.setFieldValue("termsOfService", e.target.checked)
                      }
                      disabled={formik.isSubmitting}
                      className="terms-checkbox"
                    />
                    <span className="terms-text">
                      I agree to the{" "}
                      <Link to="/terms" className="terms-link">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/policy" className="terms-link">
                        Privacy Policy
                      </Link>
                    </span>
                  </label>
                </div>

                <ChedGamingButton
                  type="submit"
                  className="signup-button"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                >
                  Create Account
                </ChedGamingButton>
              </>
            )}
          </ChedaGamingForm>

          <div className="login-section">
            <p className="login-text">
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />

      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={hideAlert}
          duration={5000}
          position="top-right"
        />
      )}
    </div>
  );
}

export default SignUp;
